/**
 * The partner-facing case page (Lloyd, Jul 21: "Let's do the per-case links").
 *
 * No login. Anyone holding the URL sees this, so it shows only where the case has got to and how it
 * ended — the server decides that, in caseShareService. Nothing here requests the client's name,
 * contact details or documents, and nothing should be added that does.
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Typography,
} from '@mui/material';
import { stageForStatus } from '../../components/CaseStatusTimeline/CaseStatusTimeline';
import ClientProgressBar from '../../components/ClientProgressBar/ClientProgressBar';
import { clientStage } from '../../components/CaseStatusTimeline/clientStages';

interface CaseProgress {
  caseNumber: string;
  citationNumber: string | null;
  statusName: string | null;
  disposition: string | null;
  isClosed: boolean;
  updatedAt: string | null;
}

const CaseProgressView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [progress, setProgress] = useState<CaseProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async (): Promise<void> => {
      if (!token) {
        setError('This link is not valid.');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.API_HOST || ''}/api/v1/case-progress/${encodeURIComponent(token)}`,
        );
        const data = await response.json().catch(() => ({}));

        if (cancelled) return;

        if (!response.ok) {
          // 403 means the link is real but the client has not given permission yet — a different
          // thing from a dead link, and the partner needs to know which so they chase the right one.
          setError(
            data.message ||
              (response.status === 403
                ? 'This case cannot be shown yet. Please contact the firm.'
                : 'This link is no longer valid.'),
          );
        } else {
          setProgress(data as CaseProgress);
        }
      } catch {
        if (!cancelled) setError('Could not load this case. Please try again later.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 1 }}>
        Case progress
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        The Law Offices of Dr. Lloyd A. Newton
      </Typography>

      <Card variant="outlined">
        <CardContent sx={{ p: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography variant="body1" color="text.secondary">
              {error}
            </Typography>
          ) : progress ? (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Case {progress.caseNumber}
              </Typography>
              {progress.citationNumber && (
                <Typography variant="body2" color="text.secondary">
                  Citation {progress.citationNumber}
                </Typography>
              )}

              <Divider sx={{ my: 3 }} />

              {/*
                The simple view (Lloyd, Aug 19). This page used to render the full staff chart —
                thirty-nine boxes, most of them dashed — to anybody holding the link. A referral
                partner has no use for "MOC" or "the 39.14 request has not been stamped", and a
                dashed box on an outsider's screen invites a question the firm then has to answer.

                Eight stages, plain words, and no shared rendering with the staff chart, so a step
                added to the journey map can never surface here by accident.
              */}
              <ClientProgressBar currentStageIndex={stageForStatus(progress.statusName)} />

              {/*
                The raw internal status is deliberately gone. It read "court respond by date
                satisfied (Pending)" — our database vocabulary, parenthesised internal state and
                all. ClientProgressBar names the stage in words written for the reader.
              */}
              {clientStage(stageForStatus(progress.statusName)) === null &&
                progress.statusName && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      This case is at a stage we do not have a plain description for yet. Please
                      contact the firm for an update.
                    </Typography>
                  </Box>
                )}

              {progress.isClosed && progress.disposition && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Outcome
                  </Typography>
                  <Typography variant="body1">{progress.disposition}</Typography>
                </Box>
              )}

              {progress.updatedAt && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
                  Last updated {new Date(progress.updatedAt).toLocaleDateString()}
                </Typography>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                This page shows case progress only. For anything else, please contact the firm.
              </Typography>
            </>
          ) : null}
        </CardContent>
      </Card>
    </Container>
  );
};

export default CaseProgressView;
